import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import Navigation from './components/Navigation/Navigation';
import Hero from './components/Hero/Hero';
import ExecutiveSummary from './components/ExecutiveSummary/ExecutiveSummary';
import InteractiveCalculator from './components/Calculator/InteractiveCalculator';
import CouplerTypes from './components/CouplerTypes/CouplerTypes';
import CaseStudies from './components/CaseStudies/CaseStudies';
import TechnicalSpecs from './components/TechnicalSpecs/TechnicalSpecs';
import InstallationGuide from './components/Installation/InstallationGuide';
import Performance3D from './components/3D/Performance3D';
import GlobalMap from './components/Map/GlobalMap';
import Footer from './components/Footer/Footer';
import ParticleBackground from './components/Effects/ParticleBackground';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import './App.css';

const AppContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  color: #ffffff;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-x: hidden;
`;

const MainContent = styled(motion.main)`
  position: relative;
  z-index: 1;
`;

const ScrollProgress = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8, #1e40af);
  z-index: 1000;
  transform-origin: 0%;
`;

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [userProfile, setUserProfile] = useState('engineer'); // engineer, executive, contractor

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const scrollPercent = scrollTop / docHeight;
      setScrollProgress(scrollPercent);

      // Update current section based on scroll position
      const sections = document.querySelectorAll('section[data-section]');
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          setCurrentSection(section.getAttribute('data-section'));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <AppContainer>
        <ParticleBackground />
        
        <ScrollProgress
          style={{ scaleX: scrollProgress }}
        />

        <Navigation 
          currentSection={currentSection}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
        />

        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={
              <MainContent>
                <Hero />
                <ExecutiveSummary userProfile={userProfile} />
                <InteractiveCalculator />
                <CouplerTypes />
                <TechnicalSpecs />
                <Performance3D />
                <InstallationGuide />
                <CaseStudies />
                <GlobalMap />
                <Footer />
              </MainContent>
            } />
          </Routes>
        </AnimatePresence>
      </AppContainer>
    </Router>
  );
}

export default App;